import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'bar3D',
  name_en: '3D Bar',
  name_zh: '3D 柱状图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 1,
    y: amount => amount > 1,
    // z: (amount) => amount > 1,
    data: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.STR, DEFINE.DATE],
      category: DEFINE.SINGLE,
      priority: DEFINE.DATE
    },
    z: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    z: string;
  };
}

/** 暂不支持legend的点击功能 */
export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec, $dom) {
    const { layout, data: dataArray, util, hook } = api;
    const {
      dataIndex = 0,
      encoding: { x, y, z }
    } = spec;

    // * -------------------------- data
    const data = dataArray[dataIndex];
    const dataMetaInfo = api.hook.parseDataMetaInfo(data);
    const transformData = util.dataTransform.transform(data.values.slice(), [
      { type: 'sort', field: y, order: 'ascending' }
    ]);
    const { xAxisData, yAxisData } = util.dataEncoding.encoding(transformData, {
      xAxisData: { field: x, transforms: [{ type: 'deDuplication' }] },
      yAxisData: {
        field: y,
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      }
    });
    const seriesData = transformData.map(item => [item[x], item[y], +item[z]]);

    // * -------------------------- chart
    const option = {
      grid3D: {
        viewControl: {
          distance: layout === util.const.LAYOUT.SMALL_1_2 ? 400 : 200
        }
      },
      tooltip: {
        // eslint-disable-next-line
        formatter: function (val) {
          const { data: dataTemp, color, seriesType, name, seriesName } = val;
          const resultZ = hook.formatDataNumberValue(dataTemp[2], {
            type: 'table',
            unit: dataMetaInfo[seriesName]?.unit
          });
          const items = [
            {
              type: util.const.SERIES_2_LEGEND_TYPE_MAP[
                seriesType as keyof (typeof util)['const']['SERIES_2_LEGEND_TYPE_MAP']
              ],
              color,
              name: '',
              value: ''
            },
            {
              name: x,
              value: dataTemp[0]
            },
            {
              name: y,
              value: dataTemp[1]
            },
            {
              name: z,
              value: `${resultZ.value + resultZ.unit}`
            }
          ];

          return hook.buildTooltip({
            config: hook.buildStandardChartPresetOption(api).tooltip,
            title: name || '',
            items
          });
        }
      },
      xAxis: null,
      yAxis: null,
      xAxis3D: {
        show: true,
        name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
        type: 'category',
        data: xAxisData
      },
      yAxis3D: {
        show: true,
        name: y + (dataMetaInfo[y]?.unit ? `(${dataMetaInfo[y].unit})` : ''),
        type: 'category',
        data: yAxisData
      },
      zAxis3D: {
        type: 'value',
        name: z + (dataMetaInfo[z]?.unit ? `(${dataMetaInfo[z].unit})` : '')
      },
      series: [
        {
          type: 'bar3D',
          data: seriesData,
          name: z,
          label: {
            formatter(data) {
              const value = data.data[2];
              const result = hook.formatDataNumberValue(value, {
                type: 'table',
                unit: dataMetaInfo[z]?.unit
              });
              return result.value + result.unit;
            }
          }
        }
      ]
    };

    return renderApi => {
      const renderParams = {
        option: renderApi.mergeChartOptionProxy(option)
      };

      renderApi.renderLegendProxy(renderParams.option.legend.data);
      renderApi.renderChartProxy(renderParams, { useECharts: true });
    };
  }
});
